<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { LabelledFieldMode } from './models';

const properties = withDefaults(
  defineProps<{
    label?: string;
    for?: string;
    mode?: LabelledFieldMode;
    // floating: the control holds a value or is open, so the label stays raised without focus
    floated?: boolean;
    class?: HTMLAttributes['class'];
  }>(),
  { label: undefined, for: undefined, mode: 'stacked', floated: false, class: undefined },
);

// Exposed to the slot so a control that `for` cannot reach (a second trigger) can aria-labelledby it
const labelId = useId();

const rootReference = useTemplateRef<HTMLElement>('rootReference');
const { focused } = useFocusWithin(rootReference);
const raised = computed(() => properties.mode === 'floating' && (properties.floated || focused.value));
</script>

<template>
  <div
    ref="rootReference"
    :class="
      cn(
        properties.mode === 'floating' ? 'grid grid-cols-1 *:col-start-1 *:row-start-1' : 'flex flex-col gap-1',
        properties.class,
      )
    "
  >
    <UiLabel
      v-if="properties.mode === 'stacked'"
      :id="labelId"
      :for="properties.for"
      class="hidden text-sm font-medium text-text-secondary-600 xl:flex"
    >
      {{ properties.label }}
    </UiLabel>

    <slot :label-id="labelId" />

    <!-- After the control so it paints above it; sized for an h-11 control. At rest it sits after the leading icon,
         raised it aligns with the icon's left edge and a 6px band (::before, stacked under the text) masks just the
         field border behind it. -->
    <UiLabel
      v-if="properties.mode === 'floating'"
      :id="labelId"
      :for="properties.for"
      :class="
        cn(
          'pointer-events-none grid self-start justify-self-start pl-2 transition-all duration-150',
          raised
            ? '-translate-y-1/2 translate-x-0 text-xs text-text-secondary-600 before:col-start-1 before:row-start-1 before:h-1.5 before:self-center before:bg-background'
            : 'translate-x-7 translate-y-3.5 text-gray-500',
        )
      "
    >
      <span class="col-start-1 row-start-1 px-1">{{ properties.label }}</span>
    </UiLabel>
  </div>
</template>
